import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithQuery, atomWithMutation, atomWithInfiniteQuery, queryClientAtom } from 'jotai-tanstack-query';
import type { InfiniteData } from '@tanstack/query-core';
import type { RecommendationClient, RecommendationConfig, RecommendationOption, SelectionKind, RecommendationGroup } from '@/types';

export function useRecommendationSettings(client: RecommendationClient, scope: string) {
  const queryClient = useAtomValue(queryClientAtom);
  const queryKey = useMemo(() => ['product-recommendations', scope], [scope]);
  const queryAtom = useMemo(() => atomWithQuery(() => ({ queryKey, queryFn: () => client.get(scope), retry: false })), [client, scope, queryKey]);
  const query = useAtomValue(queryAtom);
  const mutationAtom = useMemo(() => atomWithMutation(() => ({
    mutationFn: (config: RecommendationConfig) => client.save(scope, config),
    onSuccess: (config: RecommendationConfig) => queryClient.setQueryData(queryKey, config),
  })), [client, scope, queryClient, queryKey]);
  const mutation = useAtomValue(mutationAtom);
  const config = query.data ?? null;
  const save = async (value = config): Promise<boolean> => {
    if (!value) return false;
    try {
      await mutation.mutateAsync(value);
      return true;
    } catch { return false; }
  };
  const message = mutation.isError ? 'Could not save settings. Please retry.'
    : mutation.data?.syncStatus === 'pending' || (!mutation.isSuccess && query.data?.syncStatus === 'pending') ? 'Saved. Sync pending — apply the action again to retry.'
      : mutation.isSuccess ? 'Settings saved' : '';
  return { config, error: query.isError ? 'Could not load recommendation settings.' : '', saving: mutation.isPending, message, save, retry: () => void query.refetch() };
}
interface OptionPage { options: RecommendationOption[]; nextCursor: string | null }
export function useRecommendationOptions(client: RecommendationClient, scope: string, kind: SelectionKind, query: string, open: boolean, group?: RecommendationGroup) {
  const queryClient = useAtomValue(queryClientAtom);
  const groupKind = group?.kind;
  const groupId = group?.id;
  const groupFilter = useMemo(() => groupKind && groupId ? { kind: groupKind, id: groupId } : undefined, [groupKind, groupId]);
  const key = useMemo(() => ['recommendation-options', scope, kind, query, groupFilter?.kind, groupFilter?.id], [scope, kind, query, groupFilter]);
  const queryAtom = useMemo(() => atomWithInfiniteQuery(() => ({
    queryKey: key,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => client.options(scope, kind, query, pageParam, groupFilter),
    getNextPageParam: (lastPage: OptionPage) => lastPage.nextCursor ?? undefined,
    enabled: open,
    retry: false,
  })), [client, scope, kind, query, key, open, groupFilter]);
  const result = useAtomValue(queryAtom);
  const setOptions = (update: (options: RecommendationOption[]) => RecommendationOption[]): void => {
    queryClient.setQueryData<InfiniteData<OptionPage>>(key, (data) => data ? { ...data, pages: data.pages.map((page) => ({ ...page, options: update(page.options) })) } : data);
  };
  return { options: result.data?.pages.flatMap((page) => page.options) ?? [], error: result.isError ? 'Could not load catalogue. Please retry.' : '', loading: result.isPending, nextCursor: result.hasNextPage, busy: result.isFetchingNextPage, retry: () => void result.refetch(), loadMore: () => result.fetchNextPage(), setOptions };
}
